import { memo, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import DeleteIcon from '@atlaskit/icon/core/delete';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import Link from '@atlaskit/link';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import { Popup } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import TableTree, { Cell, Row, Rows } from '@atlaskit/table-tree';
import Tooltip from '@atlaskit/tooltip';

import {
  Account,
  Cache,
  CacheVersion,
  Repository,
} from 'src/components/pipelines/models';

import CachesIcon from '../assets/CachesIcon';
import { DOCS_DEPENDENCY_CACHES_URL } from '../constants';
import { formatDate } from '../utils';
import { sortCacheVersionsByDate } from '../utils/date';
import formatBytes from '../utils/formatBytes';

import { messages } from './Caches.i18n';
import CacheVersionRow from './CacheVersionRow';
import {
  CacheRemoveGroupButton,
  CachesDialogWrapper,
  CachesEmpty,
  CachesIconCell,
  CachesList,
  CachesName,
  CachesTitle,
  CachesVersionsLabel,
  CachesWrapper,
} from './styled';

export type CachesProps = {
  account: Account;
  repository: Repository;
  fetchingCaches: boolean;
  caches: Cache[];
  getCaches: () => void;
  deleteCache: (cacheUuid: string, cacheGroupName: string) => void;
  deleteCaches: (cacheGroupName: string) => void;
  _isDialogOpen?: boolean;
  _isDialogExpanded?: boolean;
};

export type CacheItem = {
  id: string;
  label: string;
  versions: CacheItem[];
  name?: string;
  fileSize?: string;
  createdOn?: string;
  parentName?: string;
  pipelineUuid?: string;
};

const formatVersionString = (length: number) =>
  `${length} Version${length > 1 ? 's' : ''}`;

const formatKeyHash = (hash: string) => (hash ? hash.substring(0, 7) : '-');

const transformCacheVersionToItem = (
  version: CacheVersion,
  parentName: string
): CacheItem => ({
  id: version.uuid,
  label: formatKeyHash(version.key_hash),
  versions: [],
  fileSize: formatBytes(version.file_size_bytes),
  createdOn: version.created_on,
  parentName,
  pipelineUuid: version.pipeline_uuid,
});

const Caches = ({
  account,
  repository,
  fetchingCaches,
  caches,
  getCaches,
  deleteCache,
  deleteCaches,
  _isDialogOpen = false,
  _isDialogExpanded = false,
}: CachesProps) => {
  const { formatMessage } = useIntl();

  const [isDialogOpen, setIsDialogOpen] = useState(_isDialogOpen);
  const [cacheItemToDelete, setCacheItemToDelete] = useState<CacheItem>();
  const [cacheGroupSelected, setCacheGroupSelected] = useState(false);
  const [items, setItems] = useState<CacheItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isDialogOpen) {
      if (!_isDialogOpen) {
        setIsLoading(true);
      }
      getCaches();
    }
  }, [getCaches, _isDialogOpen, isDialogOpen]);

  useEffect(() => {
    const cacheItems: CacheItem[] = caches?.map(cache => {
      const groupName = `${cache.name}: ${cache.path}`;
      const sortedVersions = sortCacheVersionsByDate(cache.versions);

      return {
        id: groupName,
        label: groupName,
        name: cache.name,
        versions: sortedVersions.map((version: CacheVersion) =>
          transformCacheVersionToItem(version, cache.name)
        ),
      };
    });
    setItems(cacheItems);
    setIsLoading(false);
  }, [caches]);

  const toggleShowingDialog = useCallback(() => {
    setIsDialogOpen(!isDialogOpen);
  }, [isDialogOpen]);

  const onCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const onCloseDeleteDialog = useCallback(() => {
    setCacheItemToDelete(undefined);
    setCacheGroupSelected(false);
  }, []);

  const onOpenDeleteDialog = useCallback((item: CacheItem) => {
    if (!item.parentName) {
      setCacheGroupSelected(true);
    }
    setCacheItemToDelete(item);
  }, []);

  const onConfirmDeleteDialog = useCallback(() => {
    if (cacheGroupSelected) {
      deleteCaches(cacheItemToDelete!.name!);
    } else {
      deleteCache(cacheItemToDelete!.id, cacheItemToDelete!.parentName!);
    }
    onCloseDeleteDialog();
  }, [
    deleteCache,
    deleteCaches,
    onCloseDeleteDialog,
    cacheItemToDelete,
    cacheGroupSelected,
  ]);

  return (
    <>
      <Popup
        content={() => (
          <CachesDialogWrapper>
            <CachesWrapper>
              <header>
                <CachesTitle>{formatMessage(messages.cachesTitle)}</CachesTitle>
              </header>
              {fetchingCaches || isLoading ? (
                <Spinner size="small" testId="loading-spinner" />
              ) : (
                <CachesList>
                  {items.length > 0 ? (
                    <TableTree>
                      <Rows
                        items={items}
                        render={(item: CacheItem) =>
                          item.versions?.length > 0 ? (
                            <Row
                              itemId={item.id}
                              hasChildren
                              items={item.versions}
                              isDefaultExpanded={_isDialogExpanded}
                            >
                              <Cell width={54}>
                                <CachesIconCell>
                                  <FolderClosedIcon
                                    label="Cache"
                                    size="medium"
                                  />
                                </CachesIconCell>
                              </Cell>
                              <Cell>
                                <CachesName>{item.label}</CachesName>
                              </Cell>
                              <Cell width={183}>
                                <CachesVersionsLabel>
                                  {formatVersionString(item.versions.length)}
                                </CachesVersionsLabel>
                              </Cell>
                              <Cell width={82}>
                                <CacheRemoveGroupButton>
                                  <Tooltip
                                    content="Delete caches"
                                    position="top"
                                  >
                                    <IconButton
                                      label="Remove"
                                      appearance="subtle"
                                      icon={DeleteIcon}
                                      onClick={() => onOpenDeleteDialog(item)}
                                    />
                                  </Tooltip>
                                </CacheRemoveGroupButton>
                              </Cell>
                            </Row>
                          ) : (
                            <CacheVersionRow
                              account={account}
                              repository={repository}
                              item={item}
                              onClickRemove={onOpenDeleteDialog}
                            />
                          )
                        }
                      ></Rows>
                    </TableTree>
                  ) : (
                    <CachesEmpty>
                      <CachesIcon />
                      <h5>{formatMessage(messages.noCachesHeader)}</h5>
                      <p>
                        <FormattedMessage
                          {...messages.noCachesDescription}
                          values={{
                            link: chunks => (
                              <Link
                                href={DOCS_DEPENDENCY_CACHES_URL}
                                target="_blank"
                              >
                                {chunks}
                              </Link>
                            ),
                          }}
                        />
                      </p>
                    </CachesEmpty>
                  )}
                </CachesList>
              )}
            </CachesWrapper>
          </CachesDialogWrapper>
        )}
        onClose={onCloseDialog}
        isOpen={isDialogOpen}
        placement="bottom-end"
        trigger={triggerProps => (
          <Button {...triggerProps} onClick={toggleShowingDialog}>
            {formatMessage(messages.cachesButton)}
          </Button>
        )}
      ></Popup>
      <ModalTransition>
        {cacheItemToDelete ? (
          <Modal onClose={onCloseDeleteDialog}>
            <ModalHeader>
              <ModalTitle appearance="danger">
                <FormattedMessage
                  {...messages.deleteCacheModalTitle}
                  values={{ pluralSuffix: cacheGroupSelected ? 's' : '' }}
                />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <p>
                {cacheGroupSelected ? (
                  <FormattedMessage
                    {...messages.deleteCacheModalBodyGroup}
                    values={{
                      cacheName: cacheItemToDelete.name,
                      bold: chunks => <strong>{chunks}</strong>,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    {...messages.deleteCacheModalBodySingle}
                    values={{
                      hasVersion: String(cacheItemToDelete.label !== '-'),
                      label: cacheItemToDelete.label,
                      parentCacheName: cacheItemToDelete.parentName,
                      creationDate: formatDate(
                        cacheItemToDelete.createdOn!,
                        'date-us-medium'
                      ),
                      bold: chunks => <strong>{chunks}</strong>,
                    }}
                  />
                )}
              </p>
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={onCloseDeleteDialog}>
                {formatMessage(messages.cancelButton)}
              </Button>
              <Button
                appearance="danger"
                onClick={onConfirmDeleteDialog}
                autoFocus
              >
                {formatMessage(messages.deleteButton)}
              </Button>
            </ModalFooter>
          </Modal>
        ) : null}
      </ModalTransition>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(Caches);
